import React, { useState, useEffect } from "react";
import { Table, Button, Toast } from "react-bootstrap";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import PaginationControls from "./PaginationControls";
import styles from "./DataTable.module.css";
import { DataTableProps, Identifiable } from "../../types/interfaces";

const endpointMapping = {
  funcionario: "inactiveFuncionario",
  organica: "inactiveOrganica",
  establecimiento: "inactiveEstablecimiento",
  reparticionGde: "inactiveReparticionGde",
  sectorGde: "inactiveSectorGde",
  inactivo: "reactivatedInactive",
};

export default function DataTable<T extends Identifiable>({
  data,
  columns,
  fetchUrl,
  ModalComponent,
  modalAction,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onReactiveClick,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onReactiveItem,
  keyActive,
  currentPage,
  itemsPerPage,
  totalItems,
  sortConfig,
  globalFilterValue,
  onPageChange,
  onItemsPerPageChange,
  onGlobalFilterChange,
  onSortChange,
}: DataTableProps<T>) {
  const [items, setItems] = useState<T[]>(data);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [itemToEdit, setItemToEdit] = useState<T | null>(null);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [itemToReactive, setItemToReactive] = useState<T | null>(null);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success");

  useEffect(() => {
    setItems(data);
  }, [data]);

  const fetchData = async () => {
  try {
    const response = await fetch(`${fetchUrl}?page=${currentPage}&pageSize=${itemsPerPage}&sortBy=${sortConfig.key}&sortOrder=${sortConfig.direction}&filter=${globalFilterValue}`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }); 
    const result = await response.json();
    setItems(result.data);
  } catch (error) {
    console.error("Error al recargar los datos:", error);
  }
};

  const handleItemAdded = async () => {
    await fetchData();
  };

  const handleItemUpdated = (updatedItem: T) => {
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
  };

  const handleItemDeleted = (deletedItem: T) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.id !== deletedItem.id)
    );
  };

  const handleItemReactivated = (reactiveItem: T) => {
    setItems((prevItems) =>
      prevItems.filter((item) => item.id !== reactiveItem.id)
    );
  };

  const getNestedValue = (obj: any, path: string) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };
  const onSort = (key: string) => {
    const direction =
      sortConfig && sortConfig.key === key && sortConfig.direction === "ASC"
        ? "DESC"
        : "ASC";
    onSortChange?.(key, direction);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onItemsPerPageChange?.(parseInt(e.target.value, 10));
  };

  const handleGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    onGlobalFilterChange?.(value);
  };

  const currentItems = items;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const renderSortIcon = (key: string): JSX.Element => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <div>
          <span className={styles.sortIconDisabled}>▲</span>
          <span className={styles.sortIconDisabled}>▼</span>
        </div>
      );
    }
    return sortConfig.direction === "ASC" ? (
      <span>▲</span>
    ) : (
      <span>▼</span>
    );
  };

  const renderTableBody = (): JSX.Element => {
    if (items.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length + 1} className="text-center">
            No se encontraron resultados para "{globalFilterValue}"
          </td>
        </tr>
      );
    }
    return <></>;
  };

  const handleAddClick = () => {
    if (onAddClick) onAddClick();
    setShowModal(true);
  };

  const handleEditClick = (item: T) => {
    setItemToEdit(item);
    if (onEditClick) onEditClick();
    setShowModal(true);
  };

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    if (onDeleteClick) onDeleteClick();
    setShowModal(true);
  };

  const handleReactiveClick = (item: T) => {
    setItemToReactive(item);
    if (onReactiveClick) onReactiveClick();
    setShowModal(true);
  };

  const showNotification = (message: string, variant: "success" | "danger") => {
    setToastMessage(message);
    setToastVariant(variant);
    setTimeout(() => setToastMessage(""), 3000);
  };

  const determineEndpoint = (url: string): string => {
    if (url.includes("allFuncionarios")) {
      return endpointMapping.funcionario;
    } else if (url.includes("allOrganicas")) {
      return endpointMapping.organica;
    } else if (url.includes("allEstablecimientos")) {
      return endpointMapping.establecimiento;
    } else if (url.includes("allReparticionesGde")) {
      return endpointMapping.reparticionGde;
    } else if (url.includes("allSectoresGde")) {
      return endpointMapping.sectorGde;
    } else if (url.includes("allInactivos")) {
      return endpointMapping.inactivo;
    }
    return "";
  };

  const apiUrl = fetchUrl ? `${process.env.REACT_APP_API_URL}/api/admin/${determineEndpoint(fetchUrl)}` : '';

  const isInactiveTable = keyActive === "inactivo";

  return (
    <div className={styles.tableContainer}>
      <div className={styles.button}>
      {!isInactiveTable && (
        <Button onClick={handleAddClick}>
          Agregar
        </Button>
        )}
      </div>
      <TableHeader
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={handleGlobalFilterChange}
        columns={columns}
        onSort={onSort}
        sortConfig={sortConfig}
        renderSortIcon={renderSortIcon}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      <div className={styles.tableWrapper}>
        <Table striped bordered hover responsive className={styles.table}>
          <thead className={styles.headerCell}>
            <tr className={styles.headerCell}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  className={`${
                    col.headerClassName
                      ? styles[col.headerClassName] || col.headerClassName
                      : ""
                  } ${styles.headerCell}`}
                >
                  <div className={styles.headerContent}>
                    <span className={styles.headerText}>{col.label}</span>
                    {renderSortIcon(col.key)}
                  </div>
                </th>
              ))}
              <th className={styles.headerActionsCell}>
                <div className={styles.headerContent}>
                  <span className={styles.headerText}>Acciones</span>
                </div>
              </th>
            </tr>
          </thead>
          <TableBody
            currentItems={currentItems}
            columns={columns}
            getNestedValue={getNestedValue}
            renderTableBody={renderTableBody}
            onEditItem={handleEditClick}
            onDeleteItem={handleDeleteClick}
            onReactiveItem={handleReactiveClick}
            keyActive={keyActive}
          />
        </Table>
      </div>
    <PaginationControls
      currentPage={currentPage}
      totalPages={totalPages}
      paginate={(page) => onPageChange?.(page)}
    />
    {showModal && ModalComponent && (
      <ModalComponent
      show={showModal}
      onHide={() => setShowModal(false)}
      showNotification={showNotification}
      onAddItem={modalAction === "add" ? async (item: T) => {
        onAddItem?.(item);
        await handleItemAdded();
      } : undefined}
      onEditItem={modalAction === "edit" ? (item: T) => {
        onEditItem?.(item);
        handleItemUpdated(item);
       } : undefined}
       onDeleteItem={modalAction === "delete" ? (item: T) => {
        onDeleteItem?.(item);
        handleItemDeleted(item);
       } : undefined}
       onReactiveItem={modalAction === "reactivated" ? (item: T) => {
        onReactiveItem?.(item);
        handleItemReactivated(item);
       } : undefined}
      item={itemToEdit || itemToDelete || itemToReactive}
      itemLabel={itemToDelete||itemToReactive ? "ítem" : "nuevo ítem"}
      apiUrl={(modalAction === "delete" || modalAction === "reactivated") ? apiUrl : ''}
      isInactiveTable={isInactiveTable}
    />
    )}
      {toastMessage && (
        <Toast bg={toastVariant} onClose={() => setToastMessage("")} className={`${styles.toastCenter} bg-success text-white`}>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      )}
    </div>
  );
}
